import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  useInvoiceDetail,
  useCreateInvoice,
  useRecordPayment,
} from '@hooks/useSalesAndBilling'
import { useSalesOrderDetail } from '@hooks/useSalesAndBilling'

const paymentSchema = z.object({
  amountPaid: z.coerce.number().positive('Amount must be greater than 0'),
})

type PaymentFormData = z.infer<typeof paymentSchema>

export function InvoiceDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const soId = searchParams.get('soId')

  const { data: invoiceData, isLoading: invoiceLoading } = useInvoiceDetail(
    id ? parseInt(id) : undefined
  )
  const { data: soData } = useSalesOrderDetail(soId ? parseInt(soId) : undefined)
  const createInvoice = useCreateInvoice()
  const recordPayment = useRecordPayment()

  const invoice = invoiceData?.data
  const salesOrder = soData?.data

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<PaymentFormData>({
    defaultValues: { amountPaid: 0 },
  })

  const handleCreateInvoice = async () => {
    try {
      setError(null)
      setSuccessMessage(null)
      await createInvoice.mutateAsync({
        salesOrderId: parseInt(soId!),
        invoiceDate: new Date().toISOString().split('T')[0],
      })
      setSuccessMessage('Invoice created successfully!')
      setTimeout(() => window.location.reload(), 1500)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create invoice')
    }
  }

  const handleRecordPayment = async (data: PaymentFormData) => {
    try {
      setError(null)
      setSuccessMessage(null)
      await recordPayment.mutateAsync({
        invoiceId: parseInt(id!),
        amountPaid: data.amountPaid,
      })
      setSuccessMessage('Payment recorded successfully!')
      reset()
      setTimeout(() => window.location.reload(), 1500)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record payment')
    }
  }

  if (!id && soId && salesOrder) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Invoice</h1>
            <p className="text-gray-600 text-sm mt-1">
              For {salesOrder.store.storeName} - {salesOrder.soNumber}
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard/sales/orders')}
            className="btn-secondary"
          >
            ← Back
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Order Summary</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Store</p>
                <p className="font-medium">{salesOrder.store.storeName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="font-medium">
                  {new Date(salesOrder.orderDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="font-semibold mb-3">Items</p>
              <div className="space-y-2">
                {salesOrder.items.map((item: any) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>
                      {item.packedGood.packSize.size} {item.packedGood.packSize.uom} x{' '}
                      {item.qtyOrdered}
                    </span>
                    <span>₹{(item.qtyOrdered * item.ratePerUnit).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4 bg-gray-50 p-3 rounded">
              <div className="flex justify-between font-bold text-lg">
                <span>Total Amount:</span>
                <span>
                  ₹
                  {salesOrder.items
                    .reduce((sum: number, item: any) => sum + item.qtyOrdered * item.ratePerUnit, 0)
                    .toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-2 justify-end">
            <button
              onClick={() => navigate('/dashboard/sales/orders')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateInvoice}
              className="btn-primary disabled:opacity-50"
              disabled={createInvoice.isPending}
            >
              {createInvoice.isPending ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (invoiceLoading) {
    return <div className="text-center py-8">Loading invoice details...</div>
  }

  if (!invoice) {
    return <div className="text-center py-8">Invoice not found</div>
  }

  const so = invoice.salesOrder
  const totalAmount = invoice.totalAmount
  const amountPaid = so.items.reduce((sum: number, item: any) => sum, 0)
  const outstandingAmount = totalAmount - (amountPaid || 0)

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{invoice.invoiceNo}</h1>
          <p className="text-gray-600 text-sm mt-1">
            Store: {so.store.storeName}
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/sales/invoices')}
          className="btn-secondary"
        >
          ← Back
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Invoice Date</div>
          <div className="font-medium mt-1">
            {new Date(invoice.invoiceDate).toLocaleDateString()}
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-600">Total Amount</div>
          <div className="text-2xl font-bold mt-1">₹{totalAmount.toLocaleString()}</div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-600">Amount Paid</div>
          <div className="text-2xl font-bold mt-1 text-green-600">₹{(amountPaid || 0).toLocaleString()}</div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-600">Outstanding</div>
          <div className="text-2xl font-bold mt-1 text-orange-600">
            ₹{outstandingAmount.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Items</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-2 font-semibold">Item</th>
                <th className="text-right px-4 py-2 font-semibold">Qty</th>
                <th className="text-right px-4 py-2 font-semibold">Rate</th>
                <th className="text-right px-4 py-2 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {so.items.map((item: any) => (
                <tr key={item.id} className="border-b">
                  <td className="px-4 py-3">
                    {item.packedGood.repacking.bulkBatch.product.name} ({item.packedGood.packSize.size} {item.packedGood.packSize.uom})
                  </td>
                  <td className="px-4 py-3 text-right">{item.qtyOrdered}</td>
                  <td className="px-4 py-3 text-right">₹{item.ratePerUnit.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">
                    ₹{(item.qtyOrdered * item.ratePerUnit).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {outstandingAmount > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Record Payment</h2>
          </div>

          <form onSubmit={handleSubmit(handleRecordPayment)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount to Receive *
                </label>
                <input
                  {...register('amountPaid')}
                  type="number"
                  min="0"
                  step="0.01"
                  max={outstandingAmount}
                  className="w-full"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  className="btn-primary disabled:opacity-50 w-full"
                  disabled={isSubmitting || recordPayment.isPending}
                >
                  {isSubmitting || recordPayment.isPending
                    ? 'Recording...'
                    : 'Record Payment'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
